import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { authorComposerService } from "../services/authorComposerService";
import { authorComposerDisplayId } from "./AuthorComposerCommonStore";
import { AuthorComposer } from "../../../api/graphql";
import { countryDisplayIdByDemonym } from "../../country/store/CountryCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import { getAuthorCollectingSocietyValues } from "../../authorCollectingSociety/store/AuthorCollectingSocietyCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { getAuthorPublisherValues } from "../../authorPublisher/store/AuthorPublisherCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { Gender } from "../../../api/graphql";
import { TaxIdType } from "../../../api/graphql";
import { CuitCertificateErrorType } from "../../../api/graphql";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { InitializeParams } from "../../../store/types";

export class AuthorComposerViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = authorComposerService;
  authorComposerInstance: AuthorComposer | null = null;
  cardState: Record<string, boolean> = {
    personalInformation: true,
    address: true,
    professionalInformation: true,
    systemDates: false,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  firstNameValue: string = "";
  lastNameValue: string = "";
  genderValue: Gender | "" = "" as Gender | "";
  taxIdTypeValue: TaxIdType | "" = "" as TaxIdType | "";
  taxIdValue: string = "";
  cuitCertificateFiles: CrudRows = {};
  cuitCertificateIssuedAtValue: Date | null = null;
  cuitCertificateErrorValue: string = "";
  cuitCertificateErrorTypeValue: CuitCertificateErrorType | "" = "" as
    | CuitCertificateErrorType
    | "";
  dniDeliveredToSadaicAtValue: Date | null = null;
  dniValidatedAtValue: Date | null = null;
  emailValue: string = "";
  phoneValue: string = "";
  nationalityService = countryService;
  nationalitySearchValue: string = "";
  nationalityIdValue: string = "";
  nationalitySelectedValue: { displayId: string; id: string } | null = null;
  dateOfBirthValue: string | null = null;
  streetAndNumberValue: string = "";
  floorValue: string = "";
  suiteValue: string = "";
  postalCodeValue: string = "";
  countryService = countryService;
  countrySearchValue: string = "";
  countryIdValue: string = "";
  countrySelectedValue: { displayId: string; id: string } | null = null;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceIdValue: string = "";
  provinceSelectedValue: { displayId: string; id: string } | null = null;
  cityValue: string = "";
  akaValue: string = "";
  ipiValue: string = "";
  managementSocietiesColumnWidths: Record<string, number | string> = {};
  managementSocietiesRows: CrudRows = {};
  publishersColumnWidths: Record<string, number | string> = {};
  publishersRows: CrudRows = {};
  createdAt_Value: Date | null = null;
  lastUpdatedAt_Value: Date | null = null;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.managementSocietiesColumnWidths = {
      __collectingSociety: 200,
      memberNumber: 200,
      __territories: 200,
    };
    this.publishersColumnWidths = {
      __publisher: 200,
      percentage: 200,
      durationInYears: 200,
      autoRenewalYears: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadAuthorComposerById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.authorComposerOne({ id });
    this.authorComposerInstance = result.authorComposerOne;
    if (this.authorComposerInstance) {
      this.loadValues(this.authorComposerInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.authorComposerInstance) {
      return authorComposerDisplayId(
        this.authorComposerInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AuthorComposerSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "nationality") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.nationalitySelectedValue = country;
      } else if (params.callerData?.fieldName === "country") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.countrySelectedValue = country;
      } else if (params.callerData?.fieldName === "province") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.provinceStateOne({
          id: params.returnData.resultId,
        });
        const provinceState = result.provinceStateOne;
        this.provinceSelectedValue = provinceState;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getGenderOptions() {
    return this.langStore.GenderOptions;
  }

  getTaxIdTypeOptions() {
    return this.langStore.TaxIdTypeOptions;
  }

  getCuitCertificateErrorTypeOptions() {
    return this.langStore.CuitCertificateErrorTypeOptions;
  }

  get nationalityValue() {
    if (this.nationalitySelectedValue) {
      return {
        id: this.nationalitySelectedValue.id,
        displayId: countryDisplayIdByDemonym(
          this.nationalitySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get countryValue() {
    if (this.countrySelectedValue) {
      return {
        id: this.countrySelectedValue.id,
        displayId: countryDisplayId(this.countrySelectedValue, this.langStore),
      };
    }
    return null;
  }

  get provinceValue() {
    if (this.provinceSelectedValue) {
      return {
        id: this.provinceSelectedValue.id,
        displayId: provinceStateDisplayId(
          this.provinceSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get managementSocietiesVisibleColumns() {
    return [
      {
        name: "__collectingSociety",
        title: this.langStore.AuthorCollectingSocietyCollectingSocietyLabel,
        width: this.managementSocietiesColumnWidths["__collectingSociety"],
      },
      {
        name: "memberNumber",
        title: this.langStore.AuthorCollectingSocietyMemberNumberLabel,
        width: this.managementSocietiesColumnWidths["memberNumber"],
      },
      {
        name: "__territories",
        title: this.langStore.AuthorCollectingSocietyTerritoriesLabel,
        width: this.managementSocietiesColumnWidths["__territories"],
      },
    ];
  }

  managementSocietiesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.managementSocietiesColumnWidths = values;
  }

  get managementSocietiesRowsDyn() {
    const entries = Object.entries(this.managementSocietiesRows).map(
      ([key, value]) => [
        key,
        {
          ...value,
          __collectingSociety: this.langStore.displayIdByLang(
            value.__collectingSociety
          ),
          __territories: this.langStore.displayIdByLang(value.__territories),
        },
      ]
    );
    return Object.fromEntries(entries);
  }

  get publishersVisibleColumns() {
    return [
      {
        name: "__publisher",
        title: this.langStore.AuthorPublisherPublisherLabel,
        width: this.publishersColumnWidths["__publisher"],
      },
      {
        name: "percentage",
        title: this.langStore.AuthorPublisherPercentageLabel,
        width: this.publishersColumnWidths["percentage"],
      },
      {
        name: "durationInYears",
        title: this.langStore.AuthorPublisherDurationInYearsLabel,
        width: this.publishersColumnWidths["durationInYears"],
      },
      {
        name: "autoRenewalYears",
        title: this.langStore.AuthorPublisherAutoRenewalYearsLabel,
        width: this.publishersColumnWidths["autoRenewalYears"],
      },
    ];
  }

  publishersSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.publishersColumnWidths = values;
  }

  get publishersRowsDyn() {
    const entries = Object.entries(this.publishersRows).map(([key, value]) => [
      key,
      {
        ...value,
        __publisher: this.langStore.displayIdByLang(value.__publisher),
      },
    ]);
    return Object.fromEntries(entries);
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "AuthorComposer",
      fieldName: fieldName,
      instanceId: this.authorComposerInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "AuthorComposer",
        fieldName: fieldName,
        instanceId: this.authorComposerInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.authorComposerInstance = null;
    this.firstNameValue = "";
    this.lastNameValue = "";
    this.genderValue = "" as Gender | "";
    this.taxIdTypeValue = "" as TaxIdType | "";
    this.taxIdValue = "";
    this.cuitCertificateFiles = {};
    this.cuitCertificateIssuedAtValue = null;
    this.cuitCertificateErrorValue = "";
    this.cuitCertificateErrorTypeValue = "" as CuitCertificateErrorType | "";
    this.dniDeliveredToSadaicAtValue = null;
    this.dniValidatedAtValue = null;
    this.emailValue = "";
    this.phoneValue = "";
    this.nationalitySearchValue = "";
    this.nationalityIdValue = "";
    this.dateOfBirthValue = null;
    this.streetAndNumberValue = "";
    this.floorValue = "";
    this.suiteValue = "";
    this.postalCodeValue = "";
    this.countrySearchValue = "";
    this.countryIdValue = "";
    this.provinceSearchValue = "";
    this.provinceIdValue = "";
    this.cityValue = "";
    this.akaValue = "";
    this.ipiValue = "";
    this.managementSocietiesRows = {};
    this.publishersRows = {};
    this.createdAt_Value = null;
    this.lastUpdatedAt_Value = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelAuthorComposer() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("authorComposer_update");
  }

  editAuthorComposer() {
    const id = this.authorComposerInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/authorComposer/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printAuthorComposer(handlePrint: () => void): void {
    handlePrint();
  }

  *closeAuthorComposer(): any {
    yield this.close();
  }

  loadValues(instance: AuthorComposer) {
    this.idValue = instance.id;
    this.firstNameValue = instance.firstName;
    this.lastNameValue = instance.lastName;
    this.genderValue = instance.gender || ("" as Gender | "");
    this.taxIdTypeValue = instance.taxIdType || ("" as TaxIdType | "");
    this.taxIdValue = instance.taxId || "";
    this.cuitCertificateFiles = instance.cuitCertificate_data
      ? {
          [instance.cuitCertificate_data.name]: {
            name: instance.cuitCertificate_data.name,
            formattedSize: formatBytes(
              instance.cuitCertificate_data?.size || 0
            ),
            mimetype: instance.cuitCertificate_data?.mimetype,
            id: instance.cuitCertificate_data.name,
            __status__: "original",
          },
        }
      : {};
    this.cuitCertificateIssuedAtValue = instance.cuitCertificateIssuedAt
      ? new Date(instance.cuitCertificateIssuedAt)
      : null;
    this.cuitCertificateErrorValue = instance.cuitCertificateError || "";
    this.cuitCertificateErrorTypeValue =
      instance.cuitCertificateErrorType ||
      ("" as CuitCertificateErrorType | "");
    this.dniDeliveredToSadaicAtValue = instance.dniDeliveredToSadaicAt
      ? new Date(instance.dniDeliveredToSadaicAt)
      : null;
    this.dniValidatedAtValue = instance.dniValidatedAt
      ? new Date(instance.dniValidatedAt)
      : null;
    this.emailValue = instance.email;
    this.phoneValue = instance.phone || "";
    this.nationalitySelectedValue = instance.nationality
      ? instance.nationality
      : null;
    this.dateOfBirthValue = diaDeCalendario(instance.dateOfBirth);
    this.streetAndNumberValue = instance.streetAndNumber || "";
    this.floorValue = instance.floor || "";
    this.suiteValue = instance.suite || "";
    this.postalCodeValue = instance.postalCode || "";
    this.countrySelectedValue = instance.country ? instance.country : null;
    this.provinceSelectedValue = instance.province ? instance.province : null;
    this.cityValue = instance.city || "";
    this.akaValue = instance.aka || "";
    this.ipiValue = instance.ipi || "";
    this.managementSocietiesRows = instance.managementSocieties
      ? Object.assign(
          {},
          ...instance.managementSocieties.map((x) => {
            const processedRow = getAuthorCollectingSocietyValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.publishersRows = instance.publishers
      ? Object.assign(
          {},
          ...instance.publishers.map((x) => {
            const processedRow = getAuthorPublisherValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.createdAt_Value = new Date(instance.createdAt_);
    this.lastUpdatedAt_Value = new Date(instance.lastUpdatedAt_);
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadAuthorComposerById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
